import type React from 'react';
import styled from 'styled-components';
import Typography from 'app/ui/components/atoms/typography';

type GenericGradientHeaderProps = {
  title: string;
  content?: string | null;
  imageUrl?: string;
  preTitle?: string | null;
  className?: string;
  gradient?: string;
};

const Wrapper = styled.div<{ $gradient?: string }>`
  display: flex;
  flex-direction: column-reverse;
  position: relative;
  width: 100vw;
  box-sizing: border-box;
  background: ${({ theme, $gradient }) => `linear-gradient(90deg, ${$gradient || theme.colors.gold} 0%, ${theme.colors.black} 100%)`};
  overflow: hidden;
  padding: ${({ theme }) => theme.spacing(8)};

  margin-left: -2.1875rem;

  ${({ theme: { media, spacing } }) =>
    `${media.medium(
      () => `
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        padding: ${spacing(6)} 2.1875rem;
        min-height: 20rem;
      `,
    )}`};

  ${({ theme: { media, spacing } }) =>
    `${media.large(
      () => `
        margin-left: -13.45rem;
        padding: ${spacing(8)} 13.45rem;
        min-height: 25rem;
      `,
    )}`};
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  z-index: 2;
  flex: 1;
  
  ${({ theme: { media } }) => `
    ${media.medium(
      () => `
        max-width: 50%;
      `,
    )}
  `};
`;

const TitleWrapper = styled.div`
  ${({ theme: { media, spacing } }) => `
    margin: 0 0 ${spacing(6)} 0;

    ${media.medium(() => 'margin-bottom: 0.6rem;')}
  `};

  h1 {
    text-transform: uppercase;
    line-height: 1em;
  }
`;

const PreTitle = styled(Typography)`
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  color: ${({ theme }) => theme.colors.white};
`;

const TextContentWrapper = styled.div`
  ${({ theme: { media, spacing } }) => `
    margin: 0 0 ${spacing(6)} 0;

    ${media.medium(() => 'margin: 0.6rem 0;')}
  `};

  ul {
    padding-left: ${({ theme: { spacing } }) => spacing(4)};
    margin-left: 0;
  }
`;

const StyledImage = styled.img`
  max-width: 75%;
  height: auto;
  object-fit: contain;
  z-index: 2;
  margin-bottom: 2rem;
  align-self: center;

  ${({ theme: { media } }) => `
    ${media.medium(
      () => `
        margin-bottom: 0;
        max-height: 15rem;
        max-width: 45%;
        align-self: center;
      `,
    )}
    ${media.large(
      () => `
        max-height: 18rem;
      `,
    )}
  `};
`;

const GenericGradientHeader: React.FC<GenericGradientHeaderProps> = ({
  title,
  content,
  imageUrl,
  preTitle,
  className,
  gradient,
}) => (
  <Wrapper className={className} $gradient={gradient}>
    <TextWrapper>
      {preTitle && <PreTitle variant="body1">{preTitle}</PreTitle>}
      <TitleWrapper>
        <Typography variant="h1">{title}</Typography>
      </TitleWrapper>
      <TextContentWrapper>{content && <Typography variant="body1" htmlContent={content || ''} />}</TextContentWrapper>
    </TextWrapper>
    {imageUrl && <StyledImage src={imageUrl} alt={title} />}
  </Wrapper>
);

export default GenericGradientHeader;
